import {CheckCircle, Info, MinusCircle, PlusCircle, WarningOctagon} from '@phosphor-icons/react';
import {kebabCase, camelCase} from 'lodash';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import {useCallback, useRef, useState} from 'react';
import {logEvent} from '@pmf/analytics';
import {updateDefaultCSS} from 'Common/api';
import {type CSSObject, type Project} from 'rendition-core';
import {useTimeoutEffect} from '@react-hookz/web';
import {Typography} from 'Components/Typography';
import {useKeyBind} from 'Hooks/useKeyBind';
import {BaseButton} from 'Components/BaseButton';

interface Props {
    project: Project;
}

export const DefaultCSS: React.FC<Props> = ({project}) => {
    const keyRef = useRef<HTMLInputElement>(null);
    const valRef = useRef<HTMLInputElement>(null);
    const [keyInput, setKeyInput] = useState('');
    const [valueInput, setValueInput] = useState('');
    const [config, setConfig] = useState<CSSObject>(
        project.defaultCSS || {
            fontFamily: 'Inter',
        }
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const user = useUserContext();

    const handleSave = async () => {
        setIsLoading(true);
        try {
            logEvent('default_css_saved', user.userId, {data: {content: config}});

            setIsLoading(true);
            await updateDefaultCSS(user.userId, config);
            setSuccess('Config successfully saved.');
            setError(null);
        } catch (err) {
            console.log('err', err);
            setError('An error occurred while saving the config.');
            setSuccess(null);
        } finally {
            setIsLoading(false);
        }
    };

    const removeStyle = useCallback(
        (key: string) => {
            const newConfig = {...config};
            delete newConfig[key];
            setConfig(newConfig);
        },
        [config]
    );

    const addStyle = useCallback(() => {
        if (keyInput && !valueInput) {
            return valRef.current?.focus();
        }
        if (keyInput && valueInput) {
            setConfig({...config, [camelCase(keyInput)]: valueInput});
            setValueInput('');
            setKeyInput('');
            keyRef.current?.focus();
        }
    }, [config, keyInput, valueInput]);

    useKeyBind('enter', addStyle, keyRef);
    useKeyBind('enter', addStyle, valRef);

    useTimeoutEffect(() => success && setSuccess(null), !!success ? 5000 : undefined);

    return (
        <div className="flex min-h-[410px] w-full max-w-md flex-col gap-2 rounded-lg bg-white p-6">
            <div className="flex h-16 items-start justify-start gap-2 rounded-lg bg-green-100 p-4 text-[10px] font-normal text-black ring-1 ring-green-400">
                {success ? (
                    <>
                        <CheckCircle weight="fill" size={18} />
                        <span className="w-full">{success}</span>
                    </>
                ) : (
                    <>
                        <Info size={18}></Info>
                        <span className="w-full">
                            This is your Global CSS configuration. You can edit it here and save it to your project.
                            Rendition will use these global styles in your compiles.
                        </span>
                    </>
                )}
            </div>

            {error && (
                <div className="flex items-center text-red-500">
                    <WarningOctagon weight="fill" size={24} className="mr-2" />
                    <span>{error}</span>
                </div>
            )}
            <div className="flex w-full flex-grow flex-col items-start justify-start gap-4 overflow-y-auto">
                {Object.keys(config).map((key) => (
                    <div key={key} className="flex w-full flex-row items-center justify-start gap-4">
                        <Typography.H6 className="py-.5 rounded-lg border border-neutral-600 px-2 text-sm font-bold">
                            {kebabCase(key)}
                        </Typography.H6>
                        <Typography.P className="">{config[key]}</Typography.P>
                        <button
                            onClick={() => removeStyle(key)}
                            className="group ml-auto appearance-none border-none disabled:opacity-50"
                        >
                            <MinusCircle size={18} className="text-red-500 group-hover:text-red-600" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-auto flex flex-col justify-end gap-2">
                <div className="box-border flex w-full flex-row items-center gap-2">
                    <input
                        ref={keyRef}
                        className="focus:shadow-outline box-border w-1/2 flex-shrink rounded border px-3 py-1 text-xs leading-tight text-gray-700 transition-all duration-200 focus:outline-none"
                        type="text"
                        placeholder="CSS Key"
                        onChange={(e) => setKeyInput(e.target.value)}
                        value={keyInput}
                    />
                    <input
                        ref={valRef}
                        className="focus:shadow-outline box-border w-1/2 flex-shrink rounded border px-3 py-1 text-xs leading-tight text-gray-700 transition-all duration-200 focus:outline-none"
                        type="text"
                        placeholder="CSS Value"
                        onChange={(e) => setValueInput(e.target.value)}
                        value={valueInput}
                    />
                    <button
                        disabled={!keyInput || !valueInput}
                        onClick={addStyle}
                        className="appearance-none border-none disabled:opacity-50"
                    >
                        <PlusCircle size={18} className="stroke-gray-700" />
                    </button>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className={`flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white transition duration-200 ease-in-out focus:outline-none ${
                        isLoading ? 'bg-blue-600' : 'bg-black'
                    } hover:bg-neutral-800`}
                >
                    {isLoading ? 'Loading...' : 'Save'}
                </button>
                <BaseButton
                    onClick={() => setConfig({})}
                    disabled={isLoading}
                    className="rounded-md bg-red-500 hover:bg-red-600"
                >
                    Clear
                </BaseButton>
            </div>
        </div>
    );
};
